import React, { FC, useMemo } from 'react';
import { Box, Grid, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { TournamentTeamEntity } from 'app/interfaces/entity';
import {
  TournamentBracketMatchGameResultReason,
  TournamentBracketParticipantResultEntity,
} from '@okletsplay/core';
import { TeamIcon } from 'shared/components/TeamIcon';
import { useStyles } from './TournamentMatchGameScore.styles';
import { BRACKET_RESULT_REASON_LABEL } from 'modules/bracket/constants';

const TOOLTIP_DISABLED_ENTER_DELAY = Math.pow(10, 10);
const TOOLTIP_ENABLED_ENTER_DELAY = 100;

interface TournamentMatchGameScoreProps {
  teams: TournamentTeamEntity[];
  results: TournamentBracketParticipantResultEntity[];
  resultReason: TournamentBracketMatchGameResultReason | undefined;
}

export const TournamentMatchGameScore: FC<TournamentMatchGameScoreProps> = ({
  teams,
  results,
  resultReason,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const isLessThanXxs = useMediaQuery(theme.breakpoints.down('x-xs'));

  const opponent1 = useMemo(() => teams[0], [teams]);
  const opponent2 = useMemo(() => teams[1], [teams]);

  const opponent1Score = useMemo(() => {
    return results?.find((res) => res?.opponentId === opponent1?.id)?.score ?? 0;
  }, [opponent1?.id, results]);

  const opponent2Score = useMemo(() => {
    return results?.find((res) => res?.opponentId === opponent2?.id)?.score ?? 0;
  }, [opponent2?.id, results]);

  const resultReasonLabel = useMemo(
    () => (resultReason ? BRACKET_RESULT_REASON_LABEL[resultReason] : ''),
    [resultReason]
  );

  const resultReasonDelay = useMemo(
    () => (resultReasonLabel ? TOOLTIP_ENABLED_ENTER_DELAY : TOOLTIP_DISABLED_ENTER_DELAY),
    [resultReasonLabel]
  );

  if (isLessThanXxs) {
    return (
      <Tooltip title={resultReasonLabel} enterDelay={resultReasonDelay} placement="top">
        <>
          <Grid container>
            <Grid item xs={5} pr={1} display="flex" justifyContent="center">
              <TeamIcon
                size={30}
                tooltip={opponent1?.name}
                borderColor={theme.palette.primary.light}
                team={teams[0]}
              />
            </Grid>
            <Grid xs={2}></Grid>
            <Grid item xs={5} pl={1} display="flex" justifyContent="center">
              <TeamIcon
                size={30}
                tooltip={opponent2?.name}
                borderColor={theme.palette.warning.light}
                team={teams[1]}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={5} pt={1.25} pr={1}>
              <Typography className={classes.score}>{opponent2Score}</Typography>
            </Grid>
            <Grid item xs={2} pt={1.25}>
              <Typography className={classes.divider}>:</Typography>
            </Grid>
            <Grid item xs={5} pt={1.25} pl={1}>
              <Typography className={classes.score}>{opponent1Score}</Typography>
            </Grid>
          </Grid>
        </>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={resultReasonLabel} enterDelay={resultReasonDelay} placement="top">
      <Box className={classes.root}>
        <TeamIcon
          size={30}
          tooltip={opponent1?.name}
          borderColor={theme.palette.primary.light}
          mr={0.5}
          team={teams[0]}
        />
        <Typography className={classes.score}>{opponent1Score}</Typography>
        <Typography className={classes.score}>:</Typography>
        <Typography className={classes.score}>{opponent2Score}</Typography>
        <TeamIcon
          size={30}
          tooltip={opponent2?.name}
          borderColor={theme.palette.warning.light}
          ml={0.5}
          team={teams[1]}
        />
      </Box>
    </Tooltip>
  );
};
